/**
 * Export des états (point hebdomadaire) en PDF professionnel
 * Avec en-tête, titre, tableau et pied de page
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const APP_NAME = "Road Alert AGEROUTE";
const MARGIN = 14;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const FOOTER_HEIGHT = 12;

export interface EtatRowPdf {
  datecreation?: string;
  libellecommune?: string;
  itineraire?: string;
  surface?: number;
  libellestatut?: string;
  chefEquipe?: string;
}

function formatDateFr(dateStr: string): string {
  if (!dateStr) return "-";
  try {
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return dateStr;
  }
}

export interface ExportEtatsPdfOptions {
  data: EtatRowPdf[];
  dateDebut: string;
  dateFin: string;
  filename?: string;
  /** Data URL du logo (ex. from canvas or fetch response) pour l'en-tête */
  logoDataUrl?: string | null;
}

const LOGO_HEADER_HEIGHT_MM = 12;
const LOGO_MAX_WIDTH_MM = 45;

export function exportEtatsPdf({ data, dateDebut, dateFin, filename = "rapport-etats.pdf", logoDataUrl }: ExportEtatsPdfOptions): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const contentWidth = PAGE_WIDTH - 2 * MARGIN;
  let currentY = MARGIN;

  // --- En-tête avec logo ---
  if (logoDataUrl) {
    try {
      const imgW = LOGO_MAX_WIDTH_MM;
      const imgH = LOGO_HEADER_HEIGHT_MM;
      doc.addImage(logoDataUrl, "PNG", MARGIN, currentY - 2, imgW, imgH);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(APP_NAME, MARGIN + imgW + 4, currentY + 5);
    } catch {
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(APP_NAME, MARGIN, currentY + 4);
    }
  } else {
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(APP_NAME, MARGIN, currentY + 4);
  }
  currentY += LOGO_HEADER_HEIGHT_MM + 2;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, currentY, PAGE_WIDTH - MARGIN, currentY);
  currentY += 10;

  // --- Titre ---
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Rapport des états - Point hebdomadaire", MARGIN, currentY);
  currentY += 8;

  // --- Période ---
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Période : du ${formatDateFr(dateDebut)} au ${formatDateFr(dateFin)}`, MARGIN, currentY);
  currentY += 6;

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Rapport généré le ${formatDateFr(new Date().toISOString().slice(0, 10))} à ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`, MARGIN, currentY);
  doc.setTextColor(0, 0, 0);
  currentY += 12;

  // --- Tableau ---
  const tableColumns = ["Date création", "Commune", "Itinéraire", "Surface (m²)", "Statut", "Chef équipe"];
  const tableRows = data.map((row) => [
    formatDateFr(row.datecreation ?? ""),
    row.libellecommune ?? "-",
    row.itineraire ?? "-",
    row.surface != null ? String(row.surface.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })) : "-",
    row.libellestatut ?? "-",
    row.chefEquipe ?? "-",
  ]);

  autoTable(doc, {
    head: [tableColumns],
    body: tableRows,
    startY: currentY,
    margin: { left: MARGIN, right: MARGIN },
    tableWidth: contentWidth,
    theme: "grid",
    headStyles: {
      fillColor: [66, 139, 202],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
    },
    bodyStyles: {
      fontSize: 8,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    didDrawPage: (data) => {
      const pageCount = doc.getNumberOfPages();
      const pageNumber = data.pageNumber;
      const footerY = PAGE_HEIGHT - FOOTER_HEIGHT;

      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.2);
      doc.line(MARGIN, footerY - 3, PAGE_WIDTH - MARGIN, footerY - 3);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(`${APP_NAME} - Rapport des états - Page ${pageNumber}/${pageCount}`, MARGIN, footerY);
      doc.text(`Rapport généré le ${formatDateFr(new Date().toISOString().slice(0, 10))}`, PAGE_WIDTH - MARGIN - 50, footerY, { align: "right" });
      doc.setTextColor(0, 0, 0);
    },
  });

  doc.save(filename);
}
